import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Loader2, ArrowLeft, Music, MapPin, Star } from 'lucide-react';
import api, { resolveImageUrl } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import Pagination from '../components/Pagination';

const ArtistsListPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [genre, setGenre] = useState('Semua');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;

  useEffect(() => {
    document.title = 'Artis — Werdi Media';
    window.scrollTo(0, 0);
    api
      .get('/artists', { params: { limit: 500 } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const genres = useMemo(
    () => ['Semua', ...Array.from(new Set(items.map((a) => a.genre).filter(Boolean)))],
    [items]
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items.filter((a) => {
      const matchQ =
        !query ||
        a.name.toLowerCase().includes(query) ||
        (a.bio || '').toLowerCase().includes(query) ||
        (a.genre || '').toLowerCase().includes(query) ||
        (a.hometown || '').toLowerCase().includes(query);
      const matchG = genre === 'Semua' || a.genre === genre;
      return matchQ && matchG;
    });
  }, [items, q, genre]);

  useEffect(() => setPage(1), [q, genre]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = useMemo(
    () => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage]
  );

  const featured = useMemo(() => items.filter((a) => a.featured).slice(0, 3), [items]);

  const goTo = (p) => {
    setPage(p);
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  return (
    <div className="App bg-[#0a0a0a] min-h-screen" data-testid="artists-list-page">
      <Navbar />

      {/* Header */}
      <section className="relative pt-32 pb-12 lg:pt-40 lg:pb-16 overflow-hidden">
        <div className="absolute top-0 -left-32 w-[500px] h-[500px] rounded-full bg-[#D4A94F]/10 blur-[120px] -z-10" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-neutral-500 hover:text-[#E9C46A] link-transition mb-6"
          >
            <ArrowLeft className="w-3 h-3" /> Beranda
          </Link>
          <div className="inline-block px-4 py-1.5 rounded-full border border-[#D4A94F]/30 bg-[#D4A94F]/5 mb-5">
            <span className="text-xs font-medium tracking-widest uppercase text-[#E9C46A]">
              Roster Artis
            </span>
          </div>
          <h1 className="text-white font-bold text-4xl md:text-6xl leading-[1.05] tracking-tight mb-4">
            Talenta <span className="gold-gradient-text">Binaan Werdi Media</span>
          </h1>
          <p className="text-neutral-400 text-lg max-w-2xl mb-10">
            Kenalan lebih dekat dengan musisi-musisi berbakat yang karyanya
            didistribusikan Werdi Media ke platform digital di seluruh dunia.
          </p>

          {/* Search + filter */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
              <Input
                placeholder="Cari nama artis, genre, atau asal..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-11 h-12 rounded-full bg-[#0f0f0f] border-white/10 focus-visible:ring-[#D4A94F] focus-visible:border-[#D4A94F] text-white"
                data-testid="artists-search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {genres.map((g) => (
                <button
                  key={g}
                  onClick={() => setGenre(g)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold btn-transition border ${
                    genre === g
                      ? 'bg-[#D4A94F] text-black border-[#D4A94F]'
                      : 'bg-transparent text-neutral-300 border-white/10 hover:border-[#D4A94F]/50 hover:text-white'
                  }`}
                  data-testid={`artists-genre-${g}`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured artists */}
      {featured.length > 0 && q === '' && genre === 'Semua' && (
        <section className="pb-14">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-2 mb-5">
              <Star className="w-4 h-4 text-[#E9C46A]" />
              <h2 className="text-white font-bold text-lg tracking-tight">
                Featured <span className="gold-gradient-text">Artist</span>
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {featured.map((a) => (
                <Link
                  key={a.id}
                  to={`/artis/${a.slug}`}
                  className="group relative rounded-3xl overflow-hidden border border-[#D4A94F]/25 hover:border-[#D4A94F]/60 card-transition h-64"
                  data-testid={`artists-featured-card-${a.slug}`}
                >
                  {a.photo && (
                    <img
                      src={resolveImageUrl(a.photo)}
                      alt={a.name}
                      className="absolute inset-0 w-full h-full object-cover btn-transition group-hover:scale-105"
                      loading="lazy"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                  <div className="absolute top-4 right-4">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-black bg-[#D4A94F] px-2.5 py-1 rounded-full flex items-center gap-1">
                      <Star className="w-2.5 h-2.5 fill-black" /> Featured
                    </span>
                  </div>
                  <div className="absolute bottom-5 left-5 right-5">
                    <p className="text-xs uppercase tracking-widest text-[#E9C46A] font-semibold mb-1">
                      {a.genre}
                    </p>
                    <h3 className="text-white font-bold text-2xl leading-tight break-words">
                      {a.name}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="flex justify-center py-24">
              <Loader2 className="w-6 h-6 text-[#E9C46A] animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24 text-neutral-500">
              Tidak ada artis yang cocok. Coba kata kunci lain.
            </div>
          ) : (
            <>
              <p className="text-neutral-500 text-sm mb-6" data-testid="artists-count">
                Menampilkan {(currentPage - 1) * PAGE_SIZE + 1}
                {'–'}
                {Math.min(currentPage * PAGE_SIZE, filtered.length)} dari {filtered.length} artis
              </p>
              <div
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5"
                data-testid="artists-grid"
              >
                {paged.map((a) => (
                  <Link
                    key={a.id}
                    to={`/artis/${a.slug}`}
                    className="group relative rounded-2xl overflow-hidden bg-[#111] border border-white/[0.06] hover:border-[#D4A94F]/40 card-transition"
                    data-testid={`artist-card-${a.slug}`}
                  >
                    <div className="aspect-[3/4] relative overflow-hidden bg-neutral-900">
                      {a.photo ? (
                        <img
                          src={resolveImageUrl(a.photo)}
                          alt={a.name}
                          className="w-full h-full object-cover btn-transition group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Music className="w-10 h-10 text-neutral-800" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-90" />
                      <div className="absolute top-3 left-3">
                        <span className="text-[10px] tracking-widest uppercase font-bold text-black bg-[#D4A94F] px-2.5 py-1 rounded-full">
                          {a.genre}
                        </span>
                      </div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <h4 className="text-white font-bold text-lg leading-tight break-words">
                          {a.name}
                        </h4>
                        {a.hometown && (
                          <p className="text-neutral-400 text-xs mt-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {a.hometown}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-neutral-400 text-xs line-clamp-2 leading-relaxed">
                        {a.bio || `${(a.discography || []).length} rilisan di Werdi Media`}
                      </p>
                      <p className="mt-2 text-[10px] uppercase tracking-widest text-[#E9C46A] font-semibold">
                        {(a.discography || []).length} rilisan →
                      </p>
                    </div>
                  </Link>
                ))}
              </div>

              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onChange={goTo}
                  testIdPrefix="artists"
                />
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ArtistsListPage;
